import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import api from '../config/axios';
import SectionCard from '../components/ui/SectionCard';

const toInput = (value) => (value ? String(value).slice(0, 16).replace(' ', 'T') : '');
const fromInput = (value) => (value ? `${value.replace('T', ' ')}:00` : '');

const operationLabel = { check_in: 'Entrada', check_out: 'Saída', save: 'Alteração', restore: 'Restauração' };

export default function PresenceWindowSettings({ eventId, activities }) {
  const [day, setDay] = useState('');
  const [subEventId, setSubEventId] = useState('');
  const [configuration, setConfiguration] = useState(null);
  const [checkInOpens, setCheckInOpens] = useState('');
  const [checkInCloses, setCheckInCloses] = useState('');
  const [checkOutOpens, setCheckOutOpens] = useState('');
  const [checkOutCloses, setCheckOutCloses] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setDay('');
    setSubEventId('');
    setConfiguration(null);
  }, [eventId]);

  const applyConfiguration = useCallback((body) => {
    setConfiguration(body);
    setDay((current) => current || body.day || '');
    setCheckInOpens(toInput(body.windows.checkIn.opensAt));
    setCheckInCloses(toInput(body.windows.checkIn.closesAt));
    setCheckOutOpens(toInput(body.windows.checkOut.opensAt));
    setCheckOutCloses(toInput(body.windows.checkOut.closesAt));
  }, []);

  const load = useCallback(async (nextDay = '', nextActivity = '') => {
    if (!eventId) return;
    setError('');
    try {
      const response = await api.get(`/hackathon/admin/events/${eventId}/presence-windows`, {
        params: { day: nextDay || undefined, subEventId: nextActivity || undefined },
      });
      applyConfiguration(response.data);
    } catch (reason) {
      setError(reason?.response?.data?.message || 'Não foi possível carregar as janelas.');
    }
  }, [applyConfiguration, eventId]);

  useEffect(() => {
    if (eventId) load('', '');
  }, [eventId, load]);

  const saveOperation = async (operation, opensAt, closesAt, current) => {
    const standard = operation === 'check_in' ? configuration.windows.standard.checkIn : configuration.windows.standard.checkOut;
    const sameAsStandard = standard && fromInput(opensAt) === standard.opensAt && fromInput(closesAt) === standard.closesAt;
    if (sameAsStandard) {
      if (current.source !== 'custom') return;
      await api.post(`/hackathon/admin/events/${eventId}/presence-windows/restore`, {
        day: configuration.day,
        subEventId: subEventId || null,
        operation,
      });
      return;
    }
    await api.put(`/hackathon/admin/events/${eventId}/presence-windows`, {
      day: configuration.day,
      subEventId: subEventId || null,
      operation,
      opensAt: fromInput(opensAt),
      closesAt: fromInput(closesAt),
      updatedAt: current.updatedAt,
    });
  };

  const save = async () => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await saveOperation('check_in', checkInOpens, checkInCloses, configuration.windows.checkIn);
      await saveOperation('check_out', checkOutOpens, checkOutCloses, configuration.windows.checkOut);
      await load(configuration.day, subEventId);
      setNotice('Janelas salvas. A próxima presença já usa estes horários.');
    } catch (reason) {
      setError(reason?.response?.data?.message || 'Não foi possível salvar.');
      await load(configuration?.day, subEventId);
    } finally {
      setBusy(false);
    }
  };

  const restore = async () => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const response = await api.post(`/hackathon/admin/events/${eventId}/presence-windows/restore`, {
        day: configuration.day,
        subEventId: subEventId || null,
      });
      applyConfiguration(response.data);
      setNotice('Horários padrão restaurados. O histórico foi mantido.');
    } catch (reason) {
      setError(reason?.response?.data?.message || 'Não foi possível restaurar.');
    } finally {
      setBusy(false);
    }
  };

  if (!eventId) return null;
  const days = configuration?.days || [];

  return (
    <SectionCard title="Configurações de presença">
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          Horário de Brasília. Estas janelas não alteram o início nem o término programados do evento ou da palestra.
          Sem configuração própria, vale a janela padrão de 15 minutos.
        </Typography>
        {error ? <Alert severity="error">{error}</Alert> : null}
        {notice ? <Alert severity="success">{notice}</Alert> : null}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
          <TextField select size="small" label="Data de presença" value={configuration?.day || day} onChange={(event) => { setDay(event.target.value); load(event.target.value, subEventId); }} sx={{ minWidth: 180 }}>
            {days.map((item) => <MenuItem key={item} value={item}>{item.split('-').reverse().join('/')}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Escopo" value={subEventId} onChange={(event) => { setSubEventId(event.target.value); load(configuration?.day || day, event.target.value); }} sx={{ minWidth: 220 }}>
            <MenuItem value="">Evento geral</MenuItem>
            {(activities || []).map((activity) => <MenuItem key={activity.id} value={String(activity.id)}>{activity.nome}</MenuItem>)}
          </TextField>
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
          <TextField fullWidth size="small" type="datetime-local" label="Abertura do check-in" InputLabelProps={{ shrink: true }} value={checkInOpens} onChange={(event) => setCheckInOpens(event.target.value)} />
          <TextField fullWidth size="small" type="datetime-local" label="Encerramento do check-in" InputLabelProps={{ shrink: true }} value={checkInCloses} onChange={(event) => setCheckInCloses(event.target.value)} />
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
          <TextField fullWidth size="small" type="datetime-local" label="Abertura do checkout" InputLabelProps={{ shrink: true }} value={checkOutOpens} onChange={(event) => setCheckOutOpens(event.target.value)} />
          <TextField fullWidth size="small" type="datetime-local" label="Encerramento do checkout" InputLabelProps={{ shrink: true }} value={checkOutCloses} onChange={(event) => setCheckOutCloses(event.target.value)} />
        </Stack>
        <Typography variant="body2">
          Check-in aplicado: {configuration?.windows.checkIn.source === 'custom' ? 'personalizado' : 'padrão'}.
          {' '}Checkout aplicado: {configuration?.windows.checkOut.source === 'custom' ? 'personalizado' : 'padrão'}.
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" disabled={busy || !configuration} onClick={save}>Salvar</Button>
          <Button variant="outlined" disabled={busy || !configuration} onClick={restore}>Restaurar padrão</Button>
        </Stack>
        <Typography variant="h6" component="h3">Histórico de alterações</Typography>
        <Table size="small" aria-label="Histórico de janelas de presença">
          <TableHead>
            <TableRow>
              <TableCell>Quando</TableCell>
              <TableCell>Quem</TableCell>
              <TableCell>Ação</TableCell>
              <TableCell>Operação</TableCell>
              <TableCell>Anterior</TableCell>
              <TableCell>Novo</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(configuration?.history || []).map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.createdAt}</TableCell>
                <TableCell>{row.actorNome || row.actorUserId}</TableCell>
                <TableCell>{row.action === 'restore' ? 'Restauração' : 'Alteração'}</TableCell>
                <TableCell>{operationLabel[row.operation] || row.operation}</TableCell>
                <TableCell>{row.previousOpensAt ? `${row.previousOpensAt} – ${row.previousClosesAt}` : '—'}</TableCell>
                <TableCell>{row.nextOpensAt ? `${row.nextOpensAt} – ${row.nextClosesAt}` : 'Padrão'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {(configuration?.history || []).length === 0 ? <Typography color="text.secondary">Nenhuma alteração neste escopo.</Typography> : null}
      </Stack>
    </SectionCard>
  );
}
